
import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "../../../services/axiosInstance";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Stepper from "../stepper/Stepper";
import AddPreReleaseForm from "../forms/AddPreReleaseForm";
import SubmitPreReleaseSection from "../forms/SubmitPreReleaseSection";
import PreReleaseCartTable from "../tables/PreReleaseCartTable";
import PreReleaseHistoryTable from "../tables/PreReleaseHistoryTable";

const PreReleasePage = () => {
  // Get branch_id from user context (sessionStorage/localStorage)
  const userStr = sessionStorage.getItem('user') || localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : {};
  const branchId = user.branch_id || 1;
  // Stepper state: 0 = Add Product, 1 = Added, 2 = Submit, 3 = Table
  const [step, setStep] = useState(0);
  const [products, setProducts] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editProduct, setEditProduct] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const [editQty, setEditQty] = useState(1);
  const [addQty, setAddQty] = useState(1);
  const [photo, setPhoto] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [locationStatus, setLocationStatus] = useState("idle");
  const [locationError, setLocationError] = useState("");
  const [locationCoords, setLocationCoords] = useState(null);

  const getDeviceDetails = () => {
    const ua = navigator.userAgent || "";
    const platform = navigator.platform || "Unknown";

    const getBrowser = () => {
      const edge = ua.match(/Edg\/(\d+\.?\d*)/);
      if (edge) return { name: "Edge", version: edge[1] };
      const chrome = ua.match(/Chrome\/(\d+\.?\d*)/);
      if (chrome) return { name: "Chrome", version: chrome[1] };
      const firefox = ua.match(/Firefox\/(\d+\.?\d*)/);
      if (firefox) return { name: "Firefox", version: firefox[1] };
      const safari = ua.match(/Version\/(\d+\.?\d*).*Safari/);
      if (safari) return { name: "Safari", version: safari[1] };
      return { name: "Unknown", version: "" };
    };

    const getOS = () => {
      if (/Windows NT/i.test(ua)) return { name: "Windows", version: (ua.match(/Windows NT ([0-9.]+)/) || [])[1] || "" };
      if (/Android/i.test(ua)) return { name: "Android", version: (ua.match(/Android\s([0-9.]+)/) || [])[1] || "" };
      if (/iPhone|iPad|iPod/i.test(ua)) return { name: "iOS", version: (ua.match(/OS\s([0-9_]+)/) || [])[1]?.replace(/_/g, ".") || "" };
      if (/Mac OS X/i.test(ua)) return { name: "macOS", version: (ua.match(/Mac OS X\s([0-9_]+)/) || [])[1]?.replace(/_/g, ".") || "" };
      if (/Linux/i.test(ua)) return { name: "Linux", version: "" };
      return { name: "Unknown", version: "" };
    };

    const browser = getBrowser();
    const os = getOS();

    return {
      device_name: platform,
      location: locationCoords
        ? `${locationCoords.lat},${locationCoords.lng} (±${Math.round(locationCoords.accuracy || 0)}m)`
        : null,
      browser_name: browser.name,
      browser_version: browser.version,
      os_name: os.name,
      os_version: os.version,
    };
  };
  // const [qtyError, setQtyError] = useState({});

  // ...existing code...
  // Fetch products and pre-release history
  const fetchData = useCallback(async () => {
    try {
      // Pass branch_id to backend for correct stock
      const productsResponse = await axios.get(`/pos/all-products?branch_id=${branchId}`);
      console.log("[PreReleasePage] Loaded products from API:", productsResponse.data);
      console.log("[PreReleasePage] First product sample:", productsResponse.data[0]);
      setProducts(productsResponse.data);

      const historyResponse = await axios.get("/pos/pre-release");
      setHistory(historyResponse.data);

      // Only show products with available warehouse stock in the dropdown
      const options = productsResponse.data
        .filter(p => p.warehouse_stock > 0)
        .map(p => ({
          value: p.id,
          label: p.name,
          isDisabled: false
        }));
      console.log("[PreReleasePage] Product options created:", options);
      setProductOptions(options);
    } catch (err) {
      console.error("[PreReleasePage] Error fetching data:", err);
      toast.error("Failed to load data.");
    }
  }, [branchId]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addItem = () => {
    if (editIndex !== null) {
      // Prevent changing to a duplicate product
      if (!editProduct || items.some((i, idx) => i.product_id === editProduct.value && idx !== editIndex)) {
        toast.error("Product already added or not selected.");
        setError("");
        return;
      }
      const editStock = products.find(p => p.id === editProduct.value)?.warehouse_stock;
      if (editStock !== undefined && editQty > editStock) {
        toast.error(`Only ${editStock} units available in warehouse.`);
        return;
      }
      // Update existing item, preserve qty
      const updatedItems = [...items];
      updatedItems[editIndex] = {
        product_id: editProduct.value,
        qty: editQty
      };
      setItems(updatedItems);
      toast.success("Item updated successfully.");
      setStep(1); // Move to 'Added' step
    } else {
      // Prevent adding duplicate products
      if (!selectedProduct || items.some(i => i.product_id === selectedProduct.value)) {
        toast.error("Product already added or not selected.");
        setError("");
        return;
      }
      if (addQty < 1) {
        toast.error("Quantity must be at least 1.");
        return;
      }
      const addStock = products.find(p => p.id === selectedProduct.value)?.warehouse_stock;
      if (addStock !== undefined && addQty > addStock) {
        toast.error(`Only ${addStock} units available in warehouse.`);
        return;
      }
      setItems([...items, {
        product_id: selectedProduct.value,
        qty: addQty
      }]);
      toast.success("Item added successfully.");
      setStep(1); // Move to 'Added' step
    }
    setEditProduct(null);
    setEditIndex(null);
    setEditQty(1);
    setAddQty(1);
    setSelectedProduct(null);
    setPhoto(null);
    setRemarks("");
  };

  const removeItem = (productId) => {
    setItems(items.filter(i => i.product_id !== productId));
    toast.success("Item removed successfully.");
  };

  const startEdit = (index) => {
    setEditIndex(index);
    setEditProduct(productOptions.find(o => o.value === items[index].product_id));
    setEditQty(items[index].qty);
  };

  const cancelEdit = () => {
    setEditProduct(null);
    setEditIndex(null);
    setEditQty(1);
    setAddQty(1);
  };

  // const updateQty = (productId, qty) => {
  //   if (qty < 1) {
  //     setQtyError(prev => ({ ...prev, [productId]: "Quantity must be at least 1." }));
  //     return;
  //   }
  //   setQtyError(prev => ({ ...prev, [productId]: null }));
  //   setItems(prevItems => prevItems.map(i =>
  //     i.product_id === productId ? { ...i, qty } : i
  //   ));
  // };

  const requestLocation = () => {
    setLocationError("");
    if (!navigator.geolocation) {
      setLocationStatus("unavailable");
      setLocationError("Geolocation is not supported on this device/browser.");
      return;
    }
    setLocationStatus("requesting");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocationCoords({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy
        });
        setLocationStatus("granted");
      },
      (err) => {
        if (err.code === 1) {
          setLocationStatus("denied");
          setLocationError("Location permission is blocked. Please allow access in your device/browser settings.");
        } else {
          setLocationStatus("unavailable");
          setLocationError("Unable to get location. Please turn on GPS and try again.");
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validate items
    if (items.length === 0) {
      toast.error("Please add at least one product.");
      setLoading(false);
      return;
    }

    setStep(2); // Move to 'Submit' step

    try {
      // Prepare form data
      const formData = new FormData();
      formData.append("branch_id", branchId); // Use actual branch_id from user context
      formData.append("remarks", remarks);
      formData.append("items", JSON.stringify(items.map(i => ({ product_id: i.product_id, qty: i.qty }))));
      if (photo) formData.append("photo", photo);
      if (locationCoords) {
        formData.append("location", JSON.stringify(locationCoords));
      }
      formData.append("device_details", JSON.stringify(getDeviceDetails()));

      // Debug logs before the request
      console.log("Submitting items:", items);
      console.log("FormData items:", formData.get("items"));

      // Submit pre-release
      await axios.post("/pos/pre-release", formData);

      toast.success("Pre-release submitted successfully.");
      setItems([]);
      setPhoto(null);
      setRemarks("");
      setStep(3); // Move to 'Table' step
      fetchData(); // Refresh history after submit
    } catch (err) {
      console.error(err);
      // Show detailed error message from backend if available
      const errorMessage = err.response?.data?.message || "Failed to submit pre-release.";
      const productName = err.response?.data?.product_name || "";
      const available = err.response?.data?.available;
      const requested = err.response?.data?.requested;

      let detailedError = errorMessage;
      if (productName && available !== undefined && requested !== undefined) {
        detailedError = `${productName}: You requested ${requested} units, but only ${available} units available in warehouse.`;
      }
      setError(detailedError);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSelectedProduct(null);
    setEditProduct(null);
    setEditIndex(null);
    setEditQty(1);
    setPhoto(null);
    setRemarks("");
    setItems([]);
    setError("");
    setLocationCoords(null);
    setLocationError("");
    setLocationStatus("idle");
    setStep(0); // Reset to 'Add Product' step
  };

  const scrollContainerRef = useRef(null);
  const sectionRefs = {
    add: useRef(null),
    cart: useRef(null),
    submit: useRef(null),
    history: useRef(null)
  };

  const handleStepClick = (nextStep) => {
    setStep(nextStep);
    const container = scrollContainerRef.current;
    const targets = [sectionRefs.add, sectionRefs.cart, sectionRefs.submit, sectionRefs.history];
    const target = targets[nextStep]?.current;
    if (!container || !target) return;

    const left = target.offsetLeft - container.offsetLeft;
    container.scrollTo({ left, behavior: "smooth" });
  };

  return (
    <div className="w-full max-w-[1800px] mx-auto p-3 md:p-5 text-base md:text-lg">
      {/* Stepper at the top */}
      <Stepper step={step} onStepClick={handleStepClick} />
      {/* 4-section layout: Add Pre-Release, Pre-Release Cart, Submit Request, My Pre-Releases */}
      <div
        className="overflow-x-auto overflow-y-auto max-h-[75vh] lg:overflow-x-visible lg:overflow-y-hidden lg:max-h-none pb-3"
        ref={scrollContainerRef}
      >
        <div className="grid grid-cols-4 gap-3 min-w-[1600px]">
        {/* Section 1: Add Pre-Release (product selection) */}
        <div ref={sectionRefs.add}>
          <AddPreReleaseForm
            products={products}
            productOptions={productOptions}
            selectedProduct={selectedProduct}
            setSelectedProduct={setSelectedProduct}
            editProduct={editProduct}
            setEditProduct={setEditProduct}
            editIndex={editIndex}
            editQty={editQty}
            setEditQty={setEditQty}
            addQty={addQty}
            setAddQty={setAddQty}
            addItem={addItem}
            cancelEdit={cancelEdit}
          />
        </div>
        {/* Section 2: Products to Add (table only) */}
        <div ref={sectionRefs.cart}>
          <PreReleaseCartTable
            items={items}
            products={products}
            startEdit={startEdit}
            removeItem={removeItem}
          />
        </div>
        {/* Section 3: Submit Request (photo, remarks, submit) */}
        <div ref={sectionRefs.submit}>
          <SubmitPreReleaseSection
            photo={photo}
            setPhoto={setPhoto}
            remarks={remarks}
            setRemarks={setRemarks}
            locationStatus={locationStatus}
            locationError={locationError}
            hasLocation={!!locationCoords}
            onRequestLocation={requestLocation}
            handleSubmit={handleSubmit}
            handleClear={handleClear}
            loading={loading}
            error={error}
          />
        </div>
        {/* Section 4: My Pre-Releases (history) */}
          <div ref={sectionRefs.history}>
            <PreReleaseHistoryTable
              history={history}
              loading={loading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreReleasePage;
